import React, { useState, useContext, useEffect } from 'react';
import * as Sentry from '@sentry/browser';
import './App.scss';
import {
  AppLayout,
  Route,
  Link,
  Icon,
  Sidebar,
  SidebarItem,
  SidebarSection,
  Topnav,
  SkipToContent,
  SkipToContentItem,
  FourOhFour,
  useResponsiveValue,
  SIDEBAR_BREAKPOINTS,
  SidebarPrioritySection,
  AppLauncher,
  AppLauncherTrigger,
  AppLauncherContent,
} from '@monash/portal-react';
import {
  EncumbranceNotification,
  MenuContext,
  portalLink,
  UserInitialsItem,
  UserInitialSection,
  ImpersonationContext,
  NotificationBadge,
  ChatBotContext,
  GenericError,
  useAppLauncherItems,
  UserInitialsTrigger,
  UserInitialsContent,
  UserInitials,
  useUserMenuAvatar,
  useUserMenuProfile,
} from '@monash/portal-frontend-common';
import Home from './components/pages/home/Home';
import SearchBar from './components/utilities/search/SearchBar';
import PreviewPanel from './components/ui/preview-panel/PreviewPanel';
import PageNotFoundWrapper from './components/ui/page-not-found-wrapper/PageNotFoundWrapper';
import { ErrorBoundary as SentryErrorBoundary } from '@sentry/react';
import SearchButton from './components/utilities/search/search-button/SearchButton';
import Category from './components/pages/category/Category';
import { SearchContext } from './components/providers/search-provider/SearchProvider';
import { Search } from './components/pages/search/Search';
import AllLinks from './components/pages/all-links/AllLinks';

const App = () => {
  const [showSidebar, setShowSidebar] = useState(true);
  const { menu, handleMenu, closeMenu, menuRef, getMenuRef } =
    useContext(MenuContext);
  const { currentUser } = useContext(ImpersonationContext);
  const { ChatBotButton } = useContext(ChatBotContext);
  const size = useResponsiveValue(SIDEBAR_BREAKPOINTS);

  const paths = ['/', '/all-links'];
  const [selectedPageIndex, setSelectedPageIndex] = useState(
    paths.indexOf(window.location.pathname)
  );
  const mainRoutes = [
    { to: '/', component: Home, exact: true },
    { to: '/all-links', component: AllLinks },
    { to: '/search', component: Search },
    { to: '/preview', component: Home },
  ];

  useEffect(() => {
    Sentry.setUser({ id: currentUser?.uid });
  }, []);

  // Sidebar - app launcher
  const [appLauncherOpen, setAppLauncherOpen] = useState(false);
  const { items: appLauncherItems } = useAppLauncherItems();

  // Sidebar - user menu
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { avatarSrc, avatarAlt } = useUserMenuAvatar({ getData: true });
  const { name, email, id, pronouns } = useUserMenuProfile({ getData: true });

  const { allCategories } = useContext(SearchContext);

  const categoryPagePaths = allCategories.map((category) => category?.path);

  const userInitialsCustomSection = (
    <UserInitialSection>
      <UserInitialsItem
        id="profile"
        href={portalLink('profile')}
        text="View my profile"
        icon={<Icon.Id />}
      />
      <UserInitialsItem
        id="courseProgress"
        href={portalLink('profile', 'course-progress')}
        text="Course progress"
        icon={<Icon.Ready />}
      />
    </UserInitialSection>
  );

  return (
    <SentryErrorBoundary fallback={<GenericError />}>
      <SkipToContent>
        <SkipToContentItem text="Top navigation" skipTo="#topNav" />
        <SkipToContentItem text="Sidebar" skipTo="#sidebar" />
        <SkipToContentItem text="Content" skipTo="#contentContainer" />
      </SkipToContent>

      <AppLayout
        topnav={
          <Topnav
            title="Links"
            onMenu={handleMenu}
            size={size}
            getMenuRef={getMenuRef}
          >
            {!['XS', 'S'].includes(size) && <SearchBar />}

            <div>
              <NotificationBadge />
              {size !== 'XS' && (
                <>
                  {['XS', 'S'].includes(size) && <SearchButton />}
                  <AppLauncher items={appLauncherItems} />
                  <ChatBotButton />
                  <UserInitials
                    buttonProps={{ avatarSrc }}
                    menuProps={{
                      avatarSrc,
                      avatarAlt,
                      name,
                      email,
                      id,
                      pronouns,
                    }}
                    appVersion={import.meta.env.VITE_VERSION}
                  >
                    {userInitialsCustomSection}
                  </UserInitials>
                </>
              )}
            </div>
          </Topnav>
        }
        sidebar={
          <Sidebar
            title="Links"
            ifMenu={menu}
            toggleMenu={handleMenu}
            closeMenu={closeMenu}
            size={size}
            menuRef={menuRef}
            selected={selectedPageIndex}
            onChange={setSelectedPageIndex}
          >
            {size === 'XS' && (
              <SidebarPrioritySection>
                <SearchButton isInSidebar />
                <AppLauncherTrigger
                  isInSidebar
                  onClick={() => {
                    setAppLauncherOpen(true);
                    handleMenu();
                  }}
                />
                <ChatBotButton isInSidebar onClick={handleMenu} />
                <UserInitialsTrigger
                  isInSidebar
                  onClick={() => {
                    setUserMenuOpen(true);
                    handleMenu();
                  }}
                  avatarSrc={avatarSrc}
                />
              </SidebarPrioritySection>
            )}
            <SidebarSection aria-label="Menu">
              <SidebarItem
                linkTo="/"
                id="tab-home"
                text="Categories"
                icon={<Icon.Layout2 />}
              />
              <SidebarItem
                linkTo="/all-links"
                id="tab-all-links"
                text="All links"
                icon={<Icon.LayoutList />}
              />
            </SidebarSection>
          </Sidebar>
        }
        content={
          <>
            {/* Sidebar controlled menus */}
            {size === 'XS' && (
              <>
                <AppLauncherContent
                  shown={appLauncherOpen}
                  setShown={setAppLauncherOpen}
                  items={appLauncherItems}
                  triggerRef={{ current: menuRef }} // menuRef is actually an elem not a ref
                />
                <UserInitialsContent
                  shown={userMenuOpen}
                  setShown={setUserMenuOpen}
                  triggerRef={{ current: menuRef }}
                  avatarSrc={avatarSrc}
                  avatarAlt={avatarAlt}
                  name={name}
                  email={email}
                  id={id}
                  pronouns={pronouns}
                  appVersion={import.meta.env.VITE_VERSION}
                >
                  {userInitialsCustomSection}
                </UserInitialsContent>
              </>
            )}

            {currentUser?.hasEncumbrances && <EncumbranceNotification />}

            {mainRoutes.map(({ component: Component, ...routeProps }) => (
              <Route {...routeProps} key={routeProps.to}>
                <SentryErrorBoundary fallback={<GenericError />}>
                  <Component />
                </SentryErrorBoundary>
              </Route>
            ))}

            {categoryPagePaths.map((page, i) => (
              <Route to={page} key={page} exact>
                <SentryErrorBoundary fallback={<GenericError />}>
                  <Category category={allCategories[i]} heroIndex={i} />
                </SentryErrorBoundary>
              </Route>
            ))}

            <PreviewPanel />

            <FourOhFour>
              <PageNotFoundWrapper
                setShowSidebar={setShowSidebar}
                action={<Link to="/">Go to Links</Link>}
              />
            </FourOhFour>
          </>
        }
        showSidebar={showSidebar}
      />
    </SentryErrorBoundary>
  );
};

export default App;
